import {
  assertDefined,
  createSignInMessageStatement,
  getCore,
  getDefaultClient,
  getNonce,
} from '@dynamic-labs-sdk/client/core';
import type { WalletConnectConnectionResult } from '@dynamic-labs-sdk/wallet-connect';
import {
  getSignClient,
  importWalletConnectAuthResponse,
} from '@dynamic-labs-sdk/wallet-connect/core';

import { evmApiChainName, evmMethods } from '../consts';
import { createWalletProviderFromEIP155Session } from '../createWalletProviderFromEIP155Session';

/**
 * Connects to a WalletConnect EVM wallet and verifies ownership through message signing.
 *
 * This function combines wallet connection and verification in a single step,
 * requiring the user to sign a message to prove wallet ownership before
 * the wallet account is added to their profile.
 *
 * If the wallet app does not support the WalletConnect authenticate method,
 * this prompts for connection and then verification as 2 separate steps.
 *
 * @see WalletConnect's docs on [session authentication](https://docs.reown.com/advanced/api/sign/dapp-usage#session-authenticate-with-recaps)
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the connected and verified wallet account.
 */
export const connectAndVerifyWithWalletConnectEvm = async (
  client = getDefaultClient()
): Promise<WalletConnectConnectionResult> => {
  const signClient = await getSignClient(client);
  const core = getCore(client);

  const universalLink = core.metadata?.universalLink;

  assertDefined(universalLink, 'Universal link is not set');

  const url = new URL(universalLink);

  assertDefined(client.projectSettings, 'Project settings unavailable');

  /**
   * Customer enabled networks, with mainnet first if available.
   */
  const evmNetworks = client.projectSettings.networks
    ?.find((network) => network.chainName === evmApiChainName)
    ?.networks?.sort((a, b) => {
      if (a.networkId === '1') return -1;
      if (b.networkId === '1') return 1;

      return 0;
    });

  assertDefined(evmNetworks, 'EVM networks not found');

  const caip2Networks = evmNetworks.map(
    (network) => `eip155:${network.networkId}`
  );

  /**
   * WalletConnect formats its own message for SIWE, so we need to provide the fields.
   * We later reconstruct what that message was so we can verify the signature in backend.
   */
  const nonce = await getNonce(client);

  /**
   * A WalletConnect connection request generates a URI and a promise.
   *
   * The URI must be handed to an external wallet application that will use it
   * to establish a connection via a WalletConnect relay server. Typically, this
   * is done by scanning a QR code or deep linking to the wallet application.
   *
   * The promise will resolve when the connection is established.
   */
  const { uri, response } = await signClient.authenticate({
    chainId: evmNetworks[0].networkId,
    chains: caip2Networks,
    domain: url.host,
    iat: new Date().toISOString(),
    methods: evmMethods as unknown as string[],
    nonce,
    requestId: core.environmentId,
    statement: createSignInMessageStatement(client),
    uri: url.toString(),
  });

  assertDefined(
    uri,
    'Failed to establish WalletConnect session: no URI returned'
  );

  /**
   * Once the approval promise resolves, the WalletConnect session is established
   * between the wallet application and the dapp (this app).
   */
  const walletAccountsPromise = response().then((authResponse) =>
    importWalletConnectAuthResponse(
      {
        authResponse,
        chain: 'EVM',
        walletProviderBuilder: createWalletProviderFromEIP155Session,
      },
      client
    )
  );

  return { approval: () => walletAccountsPromise, uri };
};
